import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { cloneDeep } from 'lodash';
import { shallow } from 'zustand/shallow';

import {
  CreateJourneyModal,
  FolderBreadcrumbText,
  LoadingSpinner,
  MemoryBoostConfigModal,
  MemoryBoostInfoModal,
  VideoPlayer,
} from '../../components/Molecules';
import { ClubSelectFolderModal } from '../../components/Popups';
import { CreateMemoryGameInfoSettingsTemplate } from '../../components/Templates';
import { WebPlanDetails } from '../../constants/allowance';
import { StaminaType } from '../../constants/currencies';
import { StationType } from '../../constants/Games/gameStation';
import { AppToastErrorOption } from '../../constants/toast';
import {
  getBibleBoostCreateVideo,
  getMemoryBoostCreateVideo,
} from '../../constants/videos';
import { usePopup } from '../../context';
import useGameCurrency from '../../hooks/useGameCurrency';
import { TranslationLocale } from '../../i18n';
import { I18nLocale } from '../../models/Base';
import { QuickCreateGameMode } from '../../models/DraftStore';
import { AlbumPartition, AlbumPartitionDisplay } from '../../models/SpaceStore';
import { WebBoosterType } from '../../models/UserStore';
import { useDraftStore } from '../../stores/DraftStore';
import { useSessionStore } from '../../stores/SessionStore';
import { useSpaceStore } from '../../stores/SpaceStore';
import { useUserStore } from '../../stores/UserStore';
import { arrayEqual } from '../../utils/array';
import { createI18nInfo, getUiLocale } from '../../utils/i18n';
import { splitIntoWords } from '../../utils/string';

enum ShowPopupState {
  CreateJoruney = 'createJoruney',
  MemoryConfig = 'memoryConfig',
  ShowInfo = 'showInfo',
  SelectFolder = 'selectFolder',
}
interface State {
  createType: QuickCreateGameMode | undefined;
  isNextDisabled: boolean;
  albumName: string;
  errorMessage: string | undefined;
  memoryBoost: boolean;
  memoryPeriod: number;
  selectedFolder: string;
  tempSelectedFolder: string;
  folders: AlbumPartition[];
  newFolderName: string;
  folderErrorMessage: string | undefined;
  newFolderPartition: AlbumPartition | undefined;
  showPopup: ShowPopupState | undefined;
  isLoading: boolean;
}

const MAX_LENGTH = 50;

const CreateMemoryGameInfoSettingsScreen: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { showPopup } = usePopup();

  const [state, setState] = useState<State>({
    createType: undefined,
    isNextDisabled: false,
    albumName: '',
    errorMessage: undefined,
    memoryBoost: true,
    memoryPeriod: 55,
    selectedFolder: '',
    tempSelectedFolder: '',
    folders: [],
    newFolderName: '',
    folderErrorMessage: undefined,
    newFolderPartition: undefined,
    showPopup: undefined,
    isLoading: false,
  });

  const activeUserId = useSessionStore((store) => store.activeUserId);
  const userAccount = useUserStore((store) =>
    store.getAccount(activeUserId ?? ''),
  );
  const userProfile = useUserStore((store) =>
    store.getProfile(activeUserId ?? ''),
  );
  const allowance = useUserStore((store) => store.getAllowance());

  const { mode, drafts, locale, albumName } = useDraftStore((store) => {
    const _mode = store.getGameMode();
    const _drafts = store.getGameDrafts();
    const _locale = store.getLocale();
    const _albumName = store.getAlbumName();
    return {
      mode: _mode,
      drafts: _drafts,
      locale: _locale,
      albumName: _albumName,
    };
  }, shallow);

  const stationDrafts = useDraftStore((store) => {
    return store.getStationDrafts();
  }, arrayEqual(shallow));

  useEffect(() => {
    if (mode) {
      setState((s) => ({ ...s, createType: mode }));
    } else {
      navigate('/create/memory-game/select-type', { replace: true });
    }
  }, [mode, navigate]);

  useEffect(() => {
    if (albumName) {
      setState((s) => ({ ...s, albumName: albumName }));
    } else {
      const draft = drafts[0];
      if (draft) {
        const answerText = draft.game.answer.text.text;
        const answerWords = splitIntoWords(answerText, locale, {
          withSpace: false,
        });
        const defaultAlbumName = answerWords
          .reduce(
            (acc, cur) => {
              if (acc.hasExceed || acc.count >= 5) {
                return acc;
              }
              if (cur.isWordLike) {
                const temp = acc.result + cur.text + ' ';
                if (temp.length <= 50) {
                  return {
                    result: temp,
                    hasExceed: acc.hasExceed,
                    count: acc.count + 1,
                  };
                } else if (acc.count === 0) {
                  return {
                    result: temp.slice(0, 50),
                    hasExceed: true,
                    count: acc.count + 1,
                  };
                } else {
                  return {
                    result: acc.result,
                    hasExceed: true,
                    count: acc.count,
                  };
                }
              }
              return acc;
            },
            { result: '', hasExceed: false, count: 0 },
          )
          .result.trim();
        setState((s) => ({ ...s, albumName: defaultAlbumName }));
      }
    }
  }, [albumName, drafts, locale]);

  const classroom = useSpaceStore((store) =>
    store.getSpace(activeUserId ?? ''),
  );

  useEffect(() => {
    if (classroom) {
      setState((s) => ({
        ...s,
        folders: classroom.getDefaultPartitions(
          userAccount?.settings.webBoosterType,
        ),
        selectedFolder: `${userAccount?.settings.webBoosterType}-${FolderBreadcrumbText.Default}`,
        tempSelectedFolder: `${userAccount?.settings.webBoosterType}-${FolderBreadcrumbText.Default}`,
      }));
    }
  }, [classroom, userAccount?.settings.webBoosterType]);

  const onChangeMemoryBoost = useCallback(
    (useMemoryBoost: boolean, period: number) => {
      setState((s) => ({
        ...s,
        memoryBoost: useMemoryBoost,
        memoryPeriod: period,
        // showPopup: ShowPopupState.CreateJoruney,
      }));
    },
    [],
  );

  const onPressMemoryBoost = useCallback(() => {
    setState((s) => ({ ...s, showPopup: ShowPopupState.MemoryConfig }));
  }, []);

  const onPressSelectPractice = useCallback(() => {
    navigate('/create/memory-game/select-games');
    useDraftStore.getState().setAlbumName(state.albumName);
  }, [navigate, state.albumName]);

  const onSetSelectedFolder = useCallback((selectedFolder: string) => {
    setState((s) => ({ ...s, tempSelectedFolder: selectedFolder }));
  }, []);

  const onFolderNameChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (state.folderErrorMessage) {
        setState((s) => ({
          ...s,
          newFolderName: event.target.value,
          folderErrorMessage: undefined,
        }));
      } else {
        setState((s) => ({ ...s, newFolderName: event.target.value }));
      }
    },
    [state.folderErrorMessage],
  );

  const onClickBreadcrumb = useCallback(() => {
    setState((s) => ({ ...s, showPopup: ShowPopupState.SelectFolder }));
  }, []);

  const onCloseBreadcrumb = useCallback(() => {
    setState((s) => ({
      ...s,
      showPopup: ShowPopupState.CreateJoruney,
      tempSelectedFolder: s.selectedFolder,
      newFolderName: s.newFolderPartition
        ? s.newFolderPartition.i18nGameSetPartitionInfo.resources['en-US']
            ?.name ?? ''
        : '',
    }));
  }, []);

  const onConfirmSelectFolder = useCallback(() => {
    const webBoosterType = userAccount?.settings.webBoosterType;

    if (state.tempSelectedFolder === `${FolderBreadcrumbText.NewFolder}`) {
      if (state.newFolderName.length > MAX_LENGTH) {
        // do nothing
      } else if (state.newFolderName && webBoosterType) {
        setState((s) => ({
          ...s,
          showPopup: ShowPopupState.CreateJoruney,
          selectedFolder: s.tempSelectedFolder,
          newFolderPartition: {
            partitionId: FolderBreadcrumbText.NewFolder,
            display: AlbumPartitionDisplay.Partition,
            webBoosterType,
            i18nGameSetPartitionInfo: createI18nInfo(I18nLocale.US, {
              name: state.newFolderName,
            }),
          },
        }));
      } else {
        setState((s) => ({
          ...s,
          folderErrorMessage: t('This field is required.'),
        }));
      }
    } else {
      setState((s) => ({
        ...s,
        showPopup: ShowPopupState.CreateJoruney,
        selectedFolder: s.tempSelectedFolder,
      }));
    }
  }, [
    state.newFolderName,
    state.tempSelectedFolder,
    t,
    userAccount?.settings.webBoosterType,
  ]);

  const onAlbumNameChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (state.errorMessage) {
        setState((s) => ({
          ...s,
          albumName: event.target.value,
          errorMessage: undefined,
        }));
      } else {
        setState((s) => ({ ...s, albumName: event.target.value }));
      }
    },
    [state.errorMessage],
  );

  const onClickNext = useCallback(() => {
    if (state.albumName.length > MAX_LENGTH) {
      // do nothing
    } else if (!state.albumName) {
      setState((s) => ({ ...s, errorMessage: t('This field is required.') }));
    } else if (!state.isNextDisabled) {
      setState((s) => ({ ...s, showPopup: ShowPopupState.CreateJoruney }));
    }
  }, [state.albumName, state.isNextDisabled, t]);

  const onClickBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent): string | undefined => {
      e.preventDefault();
      return '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const onPressCancel = useCallback(() => {
    setState((s) => ({ ...s, showPopup: undefined }));
  }, []);

  const onPressBack = useCallback(() => {
    setState((s) => ({ ...s, showPopup: ShowPopupState.CreateJoruney }));
  }, []);

  const { itemCount, albumFee } = useMemo(() => {
    const assistedGameCount = stationDrafts.filter(
      (station) =>
        station.stationType === StationType.AssistedGame &&
        station.isSelected &&
        !station.hidden,
    ).length;

    const gameCount = stationDrafts.filter(
      (station) =>
        station.stationType === StationType.Game &&
        station.isSelected &&
        !station.hidden,
    ).length;

    const challengeGameCount = stationDrafts.filter(
      (station) =>
        station.stationType === StationType.Game &&
        station.isSelected &&
        station.isForChallenge &&
        !station.hidden,
    ).length;

    const challengeCount =
      Math.max(0, Math.floor((challengeGameCount - 2) / 3)) +
      (challengeGameCount > 0 ? 1 : 0);

    // difficult quick memory game cost 2 stamina
    const gameFee =
      gameCount +
      (mode === QuickCreateGameMode.QuickMemory ? challengeGameCount : 0);

    const challengeFee = 0;
    // Math.max(0, Math.floor((challengeGameCount - 2) / 3)) +
    // (challengeGameCount > 0 ? 1 : 0);

    return {
      itemCount: assistedGameCount + gameCount + challengeCount,
      albumFee: gameFee + challengeFee,
    };
  }, [mode, stationDrafts]);

  const selectedItemCount = useMemo(() => {
    const gameCount = stationDrafts.filter(
      (station) => station.isSelected && !station.hidden,
    ).length;
    return gameCount;
  }, [stationDrafts]);

  const onCheckAlbumPartittion = useCallback(() => {
    const webBoosterType = userAccount?.settings.webBoosterType;
    if (classroom && webBoosterType) {
      let haveNewPartition = false;
      let selectedFolder = state.selectedFolder;
      const gameSetPartitions = cloneDeep(classroom.gameSetPartitions);
      const defaultIndex = gameSetPartitions.findIndex(
        (partition) =>
          partition.partitionId ===
          `${webBoosterType}-${FolderBreadcrumbText.Default}`,
      );
      const defaultPartition = state.folders[0];
      if (defaultIndex === -1 && defaultPartition) {
        gameSetPartitions.push(defaultPartition);
        haveNewPartition = true;
      }

      const isUseNewFolder =
        state.selectedFolder === `${FolderBreadcrumbText.NewFolder}`;

      if (isUseNewFolder && state.newFolderPartition) {
        const newFolderPartition = cloneDeep(state.newFolderPartition);
        newFolderPartition.partitionId = `${new Date().valueOf()}`;
        gameSetPartitions.push(newFolderPartition);
        selectedFolder = newFolderPartition.partitionId;
        haveNewPartition = true;
      }

      if (haveNewPartition) {
        return { gameSetPartitions, selectedFolder };
      }
    }
    return undefined;
  }, [
    classroom,
    state.folders,
    state.newFolderPartition,
    state.selectedFolder,
    userAccount?.settings.webBoosterType,
  ]);

  const onCreateGame = useCallback(async () => {
    setState((s) => ({ ...s, showPopup: undefined, isLoading: true }));
    const { gameSetPartitions, selectedFolder } =
      onCheckAlbumPartittion() ?? {};
    const result = await useDraftStore
      .getState()
      .createGames(
        activeUserId ?? '',
        state.albumName,
        selectedFolder ?? state.selectedFolder,
        state.memoryBoost,
        state.memoryPeriod,
        gameSetPartitions,
      );
    setState((s) => ({ ...s, isLoading: false }));
    if (result) {
      useDraftStore.getState().setJustFinishedCreate(true);
      navigate(`/club/journey`);
      navigate(`/club/${result.classroomId}/journey/${result.gameSetId}`, {
        state: { withBackState: true },
      });
    } else {
      toast(t('There is something wrong.'), AppToastErrorOption);
    }
  }, [
    activeUserId,
    navigate,
    onCheckAlbumPartittion,
    state.albumName,
    state.memoryBoost,
    state.memoryPeriod,
    state.selectedFolder,
    t,
  ]);

  useEffect(() => {
    const hasSelected = stationDrafts.some((station) => station.isSelected);
    if (hasSelected) {
      setState((s) => ({ ...s, isNextDisabled: false }));
    } else {
      setState((s) => ({ ...s, isNextDisabled: true }));
    }
  }, [stationDrafts]);

  const { checkBalance } = useGameCurrency(userAccount, userProfile, allowance);
  const createdItemCount = userProfile?.createdItemCount ?? 0;
  const { storage } = WebPlanDetails[userAccount?.activeWebPlanTier ?? 'tier0'];

  const onPressCreateGame = useCallback(() => {
    if (state.albumName.length > MAX_LENGTH) {
      // do nothing
    } else if (!state.albumName) {
      setState((s) => ({ ...s, errorMessage: t('This field is required.') }));
    } else if (itemCount + createdItemCount > storage) {
      setState((s) => ({ ...s, showPopup: undefined }));
      navigate('/not-enough-storage');
    } else {
      checkBalance(
        StaminaType.CreationStamina,
        albumFee,
        () => {
          onCreateGame().catch(console.error);
        },
        {
          onShowPopup: () => setState((s) => ({ ...s, showPopup: undefined })),
        },
      );
    }
  }, [
    albumFee,
    checkBalance,
    createdItemCount,
    itemCount,
    navigate,
    onCreateGame,
    state.albumName,
    storage,
    t,
  ]);

  const onPressInfo = useCallback(() => {
    setState((s) => ({ ...s, showPopup: ShowPopupState.ShowInfo }));
  }, []);

  const breadcrumbs = useMemo(() => {
    const folders = state.newFolderPartition
      ? [...state.folders, state.newFolderPartition]
      : state.folders;
    const selectFolder = folders.find(
      (folder) => folder.partitionId === state.selectedFolder,
    );
    if (selectFolder) {
      return [selectFolder];
    }
    return [];
  }, [state.folders, state.newFolderPartition, state.selectedFolder]);

  const onClickHelpVideo = useCallback(() => {
    const getVideo =
      userAccount?.settings.webBoosterType === WebBoosterType.Normal
        ? getMemoryBoostCreateVideo
        : getBibleBoostCreateVideo;
    showPopup(
      <VideoPlayer
        url={getVideo(getUiLocale(i18n.language as TranslationLocale))}
        playing={true}
        muted={false}
        // playIcon={<></>}
      />,
      {
        maxWidth: '100%',
        maxHeight: '100%',
        aspectRatio: '16/9',
        withCross: true,
      },
    );
  }, [i18n.language, showPopup, userAccount?.settings.webBoosterType]);

  return (
    <>
      <CreateMemoryGameInfoSettingsTemplate
        type={state.createType}
        webBoosterType={userAccount?.settings.webBoosterType}
        title={{
          value: state.albumName,
          onChange: onAlbumNameChange,
          errorMessage: state.errorMessage,
          maxLength: MAX_LENGTH,
        }}
        expectedPayment={albumFee}
        selectedItemCount={selectedItemCount}
        isNextDisabled={state.isNextDisabled}
        onClickHelpVideo={onClickHelpVideo}
        onPressSelectPractice={onPressSelectPractice}
        onClickBack={onClickBack}
        onClickNext={onClickNext}
      />
      {state.showPopup === ShowPopupState.CreateJoruney && (
        <CreateJourneyModal
          space={classroom}
          webBoosterType={userAccount?.settings.webBoosterType}
          breadcrumbs={breadcrumbs}
          // memoryBoostDays={state.memoryBoost ? state.memoryPeriod : 0}
          // title={{
          //   value: state.albumName,
          //   onChange: onAlbumNameChange,
          //   errorMessage: state.errorMessage,
          //   maxLength: MAX_LENGTH,
          // }}
          currencyType={StaminaType.CreationStamina}
          fee={albumFee}
          // selectedItemCount={selectedItemCount}
          onPrimary={onPressCreateGame}
          // onPressMemoryBoost={onPressMemoryBoost}
          // onPressSelectPractice={onPressSelectPractice}
          onClickBreadcrumb={onClickBreadcrumb}
          onPressCancel={onPressCancel}
        />
      )}
      {state.showPopup === ShowPopupState.MemoryConfig && (
        <MemoryBoostConfigModal
          useMemoryBoost={state.memoryBoost}
          memoryBoostDays={state.memoryPeriod}
          onPrimary={onChangeMemoryBoost}
          onPressCancel={onPressBack}
          onPressBack={onPressBack}
          onPressInfo={onPressInfo}
        />
      )}
      {state.showPopup === ShowPopupState.SelectFolder && classroom && (
        <ClubSelectFolderModal
          webBoosterType={userAccount?.settings.webBoosterType}
          space={classroom}
          selectedFolderId={state.tempSelectedFolder}
          folders={classroom.getDefaultPartitions(
            userAccount?.settings.webBoosterType,
          )}
          newFolder={{
            value: state.newFolderName,
            onChange: onFolderNameChange,
            errorMessage: state.folderErrorMessage,
            maxLength: MAX_LENGTH,
          }}
          onClickSelect={onSetSelectedFolder}
          onCancel={onCloseBreadcrumb}
          onConfirm={onConfirmSelectFolder}
        />
      )}
      {state.showPopup === ShowPopupState.ShowInfo && (
        <MemoryBoostInfoModal onClose={onPressMemoryBoost} />
      )}
      {state.isLoading && <LoadingSpinner loading={state.isLoading} />}
    </>
  );
};
export default CreateMemoryGameInfoSettingsScreen;
